import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, FileSearch, Server, Users } from 'lucide-react';
import type { ServerWideThrottle, ThrottleServerSummary } from '../../types/throttle';
import {
  MSPT_TEXT_CLASSES,
  describeThrottleMulti,
  formatEpochMsAgo,
  formatEpochMsDuration,
  getMsptSeverity,
} from '../../utils/throttle';
import { cn } from '../../utils/cn';
import { EmptyState } from '../ui/EmptyState';

export interface ThrottleServerRow {
  fqdn: string;
  summary: ThrottleServerSummary;
}

interface ThrottleServerTableProps {
  rows: ThrottleServerRow[];
  /** Render one section per clusterId (group order follows row order). */
  groupByCluster: boolean;
}

const FLAGGED_COUNT_TOOLTIP =
  'Chunks in WARNING or THROTTLED state. The feed is capped at the 10 worst chunks per dimension, so the real count may be higher.';

const PLAYERS_TOOLTIP =
  'Players online. A ×N badge means a server-wide population throttle is in force — the whole server runs at that multiplier.';

const detailPath = (fqdn: string) => `/chunk-throttle/${encodeURIComponent(fqdn)}`;

const OnlineDot = ({ online }: { online: boolean }) => (
  <span
    className={cn('inline-block w-2 h-2 rounded-full flex-shrink-0', online ? 'bg-green-500' : 'bg-red-500')}
    title={online ? 'Online' : 'Offline'}
  />
);

const FlaggedBadge = ({ count, since }: { count: number; since: number | null }) => {
  if (count === 0) {
    return <span className="text-glass-500 tabular-nums">0</span>;
  }
  const tooltip =
    since != null
      ? `${FLAGGED_COUNT_TOOLTIP}\nFlagged for ${formatEpochMsDuration(since)} (since ${new Date(since).toLocaleString()}).`
      : FLAGGED_COUNT_TOOLTIP;
  return (
    <span
      title={tooltip}
      className="badge badge-amber cursor-default"
    >
      <AlertTriangle className="w-3 h-3" />
      {count}
    </span>
  );
};

const DumpBadge = ({ summary }: { summary: ThrottleServerSummary }) => {
  if (summary.dumpCount === 0) {
    return <span className="text-glass-500 tabular-nums">—</span>;
  }
  const tooltipParts = [
    `${summary.dumpCount} live performance dump(s) — staff-triggered deep dives; each expires 24 h after it was taken.`,
  ];
  if (summary.lastDumpAt != null) {
    tooltipParts.push(`Newest taken ${formatEpochMsAgo(summary.lastDumpAt)}.`);
  }
  if (summary.worstDumpChunkMspt != null) {
    tooltipParts.push(`Worst dumped chunk: ${summary.worstDumpChunkMspt.toFixed(1)} ms/t.`);
  }
  return (
    <span
      title={tooltipParts.join('\n')}
      className="badge badge-info cursor-default"
    >
      <FileSearch className="w-3 h-3" />
      {summary.dumpCount}
    </span>
  );
};

/** Population throttle currently in force server-wide (nothing when the server is below every tier). */
const ServerThrottleBadge = ({ throttle }: { throttle: ServerWideThrottle | null | undefined }) => {
  if (!throttle) return null;
  return (
    <span
      title={`Population throttle "${throttle.id}" is in force: at ${throttle.minPlayerCount}+ players the whole server runs at ${describeThrottleMulti(throttle.throttleMulti)}.`}
      className="badge badge-amber font-semibold cursor-default flex-shrink-0"
    >
      ×{throttle.throttleMulti}
    </span>
  );
};

const MsptValue = ({ mspt, online }: { mspt: number | null; online: boolean }) => {
  if (mspt == null) {
    return <span className="text-glass-500 tabular-nums">—</span>;
  }
  return (
    <span
      className={cn(
        'font-medium tabular-nums',
        online ? MSPT_TEXT_CLASSES[getMsptSeverity(mspt)] : 'text-glass-500',
      )}
    >
      {mspt.toFixed(1)} ms
    </span>
  );
};

const ClusterTag = ({ summary }: { summary: ThrottleServerSummary }) => {
  if (!summary.clusterId && !summary.serverId) return null;
  return (
    <span className="chip flex-shrink-0">
      {[summary.clusterId, summary.serverId].filter(Boolean).join(' · ')}
    </span>
  );
};

/** Fleet index as a desktop table / mobile card list. Rows navigate to the server detail page. */
export const ThrottleServerTable = ({ rows, groupByCluster }: ThrottleServerTableProps) => {
  const navigate = useNavigate();

  const groups = useMemo(() => {
    if (!groupByCluster) {
      return [{ clusterId: null as string | null, rows }];
    }
    const byCluster = new Map<string, ThrottleServerRow[]>();
    for (const row of rows) {
      const clusterKey = row.summary.clusterId ?? 'Unclustered';
      const existing = byCluster.get(clusterKey);
      if (existing) {
        existing.push(row);
      } else {
        byCluster.set(clusterKey, [row]);
      }
    }
    return Array.from(byCluster.entries()).map(([clusterId, clusterRows]) => ({
      clusterId: clusterId as string | null,
      rows: clusterRows,
    }));
  }, [rows, groupByCluster]);

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={Server}
        title="No servers match the current filters."
        compact
      />
    );
  }

  const renderDesktopRows = (groupRows: ThrottleServerRow[]) =>
    groupRows.map(({ fqdn, summary }) => (
      <tr
        key={fqdn}
        onClick={() => navigate(detailPath(fqdn))}
        className={cn(
          'cursor-pointer',
          !summary.online && 'opacity-60',
        )}
      >
        <td className="td">
          <div className="flex items-center gap-2 min-w-0">
            <OnlineDot online={summary.online} />
            <span className="text-white font-medium font-mono text-sm truncate">{fqdn}</span>
            <ClusterTag summary={summary} />
          </div>
        </td>
        <td className="td text-glass-300 tabular-nums text-right">
          {summary.tps != null ? summary.tps.toFixed(1) : '—'}
        </td>
        <td className="td text-right">
          <MsptValue mspt={summary.mspt} online={summary.online} />
        </td>
        <td className="td text-glass-300">
          <div className="flex items-center gap-1.5">
            <span className="tabular-nums">
              {summary.playerCount != null
                ? `${summary.playerCount}/${summary.maxPlayers ?? '?'}`
                : '—'}
            </span>
            <ServerThrottleBadge throttle={summary.serverWideThrottle} />
          </div>
        </td>
        <td className="td">
          <FlaggedBadge count={summary.flaggedChunkCount} since={summary.flaggedSince} />
        </td>
        <td className="td text-glass-300 tabular-nums text-right">
          {summary.worstFlaggedChunkMspt != null
            ? `${summary.worstFlaggedChunkMspt.toFixed(1)} ms`
            : '—'}
        </td>
        <td className="td">
          <DumpBadge summary={summary} />
        </td>
        <td className="td text-xs text-glass-400">
          {summary.online ? (
            <span className="text-green-400">Online</span>
          ) : summary.lastSeenAt != null ? (
            <span title={new Date(summary.lastSeenAt).toLocaleString()}>
              Last seen {formatEpochMsAgo(summary.lastSeenAt)}
            </span>
          ) : (
            <span>Offline</span>
          )}
        </td>
      </tr>
    ));

  const renderMobileCards = (groupRows: ThrottleServerRow[]) =>
    groupRows.map(({ fqdn, summary }) => (
      <button
        key={fqdn}
        onClick={() => navigate(detailPath(fqdn))}
        className={cn(
          'glass-card glass-card-interactive p-3 w-full text-left touch-manipulation',
          !summary.online && 'opacity-60',
        )}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <OnlineDot online={summary.online} />
            <span className="text-white font-medium font-mono text-sm truncate">{fqdn}</span>
          </div>
          <div className="flex items-center gap-2">
            <DumpBadge summary={summary} />
            <FlaggedBadge count={summary.flaggedChunkCount} since={summary.flaggedSince} />
          </div>
        </div>
        <div className="mt-2 flex items-center justify-between text-xs">
          <span className="text-glass-400">
            {[summary.clusterId, summary.serverId].filter(Boolean).join(' · ') || '—'}
          </span>
          <div className="flex items-center gap-3">
            <span className="text-glass-400 tabular-nums">
              TPS {summary.tps != null ? summary.tps.toFixed(1) : '—'}
            </span>
            <MsptValue mspt={summary.mspt} online={summary.online} />
            <span className="flex items-center gap-1 text-glass-400 tabular-nums">
              <Users className="w-3 h-3" />
              {summary.playerCount != null
                ? `${summary.playerCount}/${summary.maxPlayers ?? '?'}`
                : '—'}
            </span>
            <ServerThrottleBadge throttle={summary.serverWideThrottle} />
          </div>
        </div>
        {!summary.online && summary.lastSeenAt != null && (
          <p className="mt-1.5 text-[11px] text-glass-500">
            Last seen {formatEpochMsAgo(summary.lastSeenAt)}
          </p>
        )}
      </button>
    ));

  return (
    <div className="space-y-4">
      {groups.map((group) => {
        const flaggedInGroup = group.rows.reduce(
          (sum, row) => sum + row.summary.flaggedChunkCount,
          0,
        );
        return (
          <div key={group.clusterId ?? '__all__'}>
            {group.clusterId != null && (
              <div className="flex items-center justify-between gap-3 px-1 mb-2">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-white">
                  {group.clusterId}
                  <span className="badge badge-neutral">
                    {group.rows.length} server{group.rows.length === 1 ? '' : 's'}
                  </span>
                </h3>
                {flaggedInGroup > 0 && (
                  <span className="text-xs text-amber-300 tabular-nums">
                    {flaggedInGroup} flagged chunk{flaggedInGroup === 1 ? '' : 's'}
                  </span>
                )}
              </div>
            )}

            {/* Desktop table */}
            <div className="hidden md:block table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th className="th">Server</th>
                    <th className="th text-right">TPS</th>
                    <th className="th text-right" title="Milliseconds per tick — the tick budget is 50 ms">
                      MSPT
                    </th>
                    <th className="th" title={PLAYERS_TOOLTIP}>
                      Players
                    </th>
                    <th className="th" title={FLAGGED_COUNT_TOOLTIP}>
                      Flagged
                    </th>
                    <th className="th text-right" title="Highest MSPT among flagged chunks">
                      Worst chunk
                    </th>
                    <th className="th" title="Live staff-triggered performance dumps (expire after 24 h)">
                      Dumps
                    </th>
                    <th className="th">Status</th>
                  </tr>
                </thead>
                <tbody>{renderDesktopRows(group.rows)}</tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-2">{renderMobileCards(group.rows)}</div>
          </div>
        );
      })}
    </div>
  );
};
